import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Loader2, Check, X } from 'lucide-react';
import { rb } from './theme';
import { resumeService } from '../../services/resumeService';

// "Improve with AI" for a single textarea value. Shows the enhanced text
// inline with Accept / Keep mine so the user always stays in control.
export default function AIEnhanceButton({ sessionId, sectionType, text, onAccept }) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const enhance = async () => {
    if (!text || !text.trim() || loading) return;
    setLoading(true);
    setError(null);
    try {
      const res = await resumeService.enhanceContent(sessionId, sectionType, text);
      setResult(res);
    } catch (err) {
      setError(
        err?.response?.status === 429
          ? 'AI is busy right now — try again in a moment.'
          : "Couldn't improve this text right now. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={enhance}
        disabled={loading || !text?.trim()}
        className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${rb.glassSoft} text-violet-200 hover:text-white`}
      >
        {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 text-violet-300 rb-pulse" />}
        {loading ? 'Improving…' : 'Improve with AI'}
      </button>

      {error && <p className="mt-2 text-xs text-red-400">{error}</p>}

      <AnimatePresence>
        {result && (
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 6 }}
            className={`mt-3 rounded-2xl p-3.5 ${rb.glass} ${rb.gradRing}`}
          >
            <p className="text-[11px] font-bold uppercase tracking-wide mb-1.5 rb-grad-text inline-flex items-center gap-1">
              <Sparkles className="w-3 h-3 text-violet-300" /> AI suggestion
            </p>
            <p className="text-sm whitespace-pre-wrap text-slate-200">{result.enhanced_content}</p>
            <div className="flex gap-2 mt-3">
              <button
                type="button"
                onClick={() => { onAccept(result.enhanced_content); setResult(null); }}
                className={`inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full ${rb.glowBtn}`}
              >
                <Check className="w-3.5 h-3.5" /> Use this
              </button>
              <button
                type="button"
                onClick={() => setResult(null)}
                className={`inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full ${rb.secondaryBtn}`}
              >
                <X className="w-3.5 h-3.5" /> Keep mine
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
